import { AuthRepository } from '../../domain/ports/AuthRepository';
import { Admin } from '../../domain/models/Admin';
import { AuthToken } from '../../domain/models/AuthToken';
import { createLoginCredentials } from '../../domain/models/LoginCredentials';

/**
 * AuthService
 * Application service that orchestrates authentication operations
 */
export class AuthService {
  constructor(
    private authRepository: AuthRepository,
    private onTokenReceived: (token: AuthToken) => void,
    private onLogout: () => void,
  ) {}

  /**
   * Authenticate a user with email and password
   * Returns both the admin and the token for scheduling refresh
   */
  async login(email: string, password: string): Promise<{ admin: Admin; token: AuthToken }> {
    // Create credentials value object (validates input)
    const credentials = createLoginCredentials(email, password);

    // Authenticate and get token
    const token = await this.authRepository.login(credentials);

    // Store token via callback
    this.onTokenReceived(token);

    // Fetch authenticated admin
    const admin = await this.authRepository.getCurrentAdmin();

    return { admin, token };
  }

  /**
   * Refresh the access token
   */
  async refreshToken(): Promise<AuthToken> {
    const token = await this.authRepository.refreshToken();
    this.onTokenReceived(token);
    return token;
  }

  /**
   * Log out the current user
   * Always clears local state, even if server logout fails
   */
  async logout(): Promise<void> {
    try {
      await this.authRepository.logout();
    } catch (error) {
      // Logout errors are non-critical - always clear local state
      console.error('Logout request failed:', error);
    } finally {
      this.onLogout();
    }
  }

  /**
   * Get the current authenticated admin
   */
  async getCurrentAdmin(): Promise<Admin> {
    return await this.authRepository.getCurrentAdmin();
  }
}
